import { useState } from 'react';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Download, MessageSquareReply, Search } from 'lucide-react';
import { formatFormValue, type FormField, type FormSubmissionQuery, type FormTemplate } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { messageTimestamp } from '@/lib/format';
import { useUi } from '@/lib/store';
import { DialogFooter, Button, EmptyState, ErrorBox, Input, Modal, Select, Spinner } from '@/components/ui';
import { useBuildings } from '@/features/buildings/common';
import { useTeams } from '@/lib/hooks';
import { StatusPill } from './FormPost';
import { DateRangeFilter, useDateRangeParam } from '@/components/ui/DateRangeFilter';
import type { RangePreset } from '@/lib/dateRange';

const FILTERABLE: FormField['type'][] = ['short_text', 'long_text', 'number', 'single_select', 'multi_select', 'date', 'checkbox', 'person', 'building', 'department', 'group'];

const SUBMITTED_PRESETS: RangePreset[] = ['any', 'today', 'yesterday', 'last-7-days', 'last-30-days', 'this-week', 'last-week', 'this-month', 'last-month', 'custom'];

/** Table view of a form channel: filter, sort by any field, export CSV. */
export function SubmissionsTable({ conversationId, template }: { conversationId: string; template: FormTemplate }) {
  const toast = useUi(s => s.toast);
  const openThread = useUi(s => s.openThread);
  const buildings = useBuildings();
  const { teams } = useTeams();
  const [q0, setQ] = useState<FormSubmissionQuery>({ sort: 'created_at', dir: 'desc' });
  // t294u: when it was submitted (UK days), in the URL (?submitted=); Any time by default.
  const [dates, setDates] = useDateRangeParam({ key: 'submitted', fallback: 'any', presets: SUBMITTED_PRESETS });
  const q: FormSubmissionQuery = { ...q0, from: dates.from || undefined, to: dates.to || undefined };
  const [text, setText] = useState('');
  const data = useQuery({ queryKey: ['form-submissions', conversationId, q], queryFn: () => api.formSubmissions(conversationId, q), placeholderData: p => p, refetchInterval: 20_000 });
  const columns = data.data?.columns ?? template.fields;
  const filterField = columns.find(c => c.id === q.field_id);
  const sortBy = (key: string) => setQ(x => ({ ...x, sort: key, dir: x.sort === key && x.dir === 'desc' ? 'asc' : 'desc' }));
  const Th = ({ id, children }: { id: string; children: React.ReactNode }) => (
    <th className="whitespace-nowrap px-3 py-2 text-left">
      <button onClick={() => sortBy(id)} className="flex items-center gap-1 font-semibold hover:text-slate-900 dark:hover:text-white">
        {children}{q.sort === id && (q.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
  const exportCsv = async () => {
    try {
      const csv = await api.formSubmissionsCsv(conversationId, q);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${template.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const valueOptions = filterField?.type === 'single_select' || filterField?.type === 'multi_select' ? filterField.options ?? []
    : filterField?.type === 'building' ? (buildings.data?.buildings ?? []).map(b => b.name)
    : filterField?.type === 'department' || filterField?.type === 'group' ? teams.filter(t => t.kind === filterField.type && !t.archived_at).map(t => t.name).sort() : filterField?.type === 'checkbox' ? ['true', 'false'] : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-5 py-2.5 dark:border-slate-800">
        <form className="relative" onSubmit={e => { e.preventDefault(); setQ(x => ({ ...x, q: text.trim() || undefined })); }}>
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input aria-label="Search submissions" className="h-9 w-56 pl-8" placeholder="Search submissions…" value={text} onChange={e => setText(e.target.value)} onBlur={() => setQ(x => ({ ...x, q: text.trim() || undefined }))} />
        </form>
        <DateRangeFilter value={dates} onChange={setDates} presets={SUBMITTED_PRESETS} label="Submitted" data-testid="submission-dates" />
        {template.statuses.length > 0 && (
          <Select aria-label="Status filter" className="h-9 w-40" value={q.status ?? ''} onChange={e => setQ(x => ({ ...x, status: e.target.value || undefined }))}>
            <option value="">All statuses</option>{template.statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </Select>
        )}
        <Select aria-label="Filter by field" className="h-9 w-44" value={q.field_id ?? ''} onChange={e => setQ(x => ({ ...x, field_id: e.target.value || undefined, field_value: undefined }))}>
          <option value="">Filter by field…</option>{columns.filter(c => FILTERABLE.includes(c.type)).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </Select>
        {filterField && (valueOptions ? (
          <Select aria-label="Field value" className="h-9 w-44" value={q.field_value ?? ''} onChange={e => setQ(x => ({ ...x, field_value: e.target.value || undefined }))}>
            <option value="">Any</option>{valueOptions.map(o => <option key={o} value={o}>{filterField.type === 'checkbox' ? (o === 'true' ? 'Yes' : 'No') : o}</option>)}
          </Select>
        ) : (
          <Input aria-label="Field value" className="h-9 w-44" type={filterField.type === 'date' ? 'date' : 'text'} placeholder="contains…" value={q.field_value ?? ''}
            onChange={e => setQ(x => ({ ...x, field_value: e.target.value || undefined }))} />
        ))}
        <span className="ml-auto text-xs text-slate-500">{data.data ? `${data.data.submissions.length} of ${data.data.total}` : ''}</span>
        <Button variant="secondary" size="sm" onClick={() => void exportCsv()}><Download className="h-4 w-4" />Export CSV</Button>
      </div>
      <div className="scroll-thin min-h-0 flex-1 overflow-auto">
        {data.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : data.isError ? <div className="p-6"><ErrorBox error={data.error} /></div>
          : !data.data?.submissions.length ? <div className="p-8"><EmptyState title="No submissions match" /></div> : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-[1] bg-slate-50 text-xs text-slate-500 dark:bg-slate-900">
                <tr>
                  <Th id="created_at">Submitted</Th><Th id="sender">By</Th>
                  {template.statuses.length > 0 && <Th id="status">Status</Th>}
                  {columns.map(c => <Th key={c.id} id={c.id}>{c.label}</Th>)}
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.data.submissions.map(s => (
                  <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/60">
                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{messageTimestamp(s.created_at)}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium"><PersonButton userId={s.sender_id} name={s.sender_name} className="rounded hover:underline">{s.sender_name}</PersonButton></td>
                    {template.statuses.length > 0 && <td className="px-3 py-2"><StatusPill status={s.status} statuses={template.statuses} /></td>}
                    {columns.map(c => <td key={c.id} className="max-w-64 truncate px-3 py-2" title={formatFormValue(c, s.values[c.id], s.labels)}>{formatFormValue(c, s.values[c.id], s.labels) || <span className="text-slate-300">-</span>}</td>)}
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <button onClick={() => openThread(s.message_id)} className="flex items-center gap-1 text-xs font-semibold link">
                        <MessageSquareReply className="h-3.5 w-3.5" />{s.reply_count ? `${s.reply_count} ${s.reply_count === 1 ? 'reply' : 'replies'}` : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {data.data?.capped && <div className="p-3 text-center text-xs text-slate-500">Showing the newest 2,000 submissions. Narrow the dates or use the CSV export of a filtered view.</div>}
      </div>
    </div>
  );
}

/** Channel menu → "Channel form…": choose which form every new post must use. */
export function ChannelFormModal({ conversationId, workspaceId, current, onClose, onSaved }: { conversationId: string; workspaceId: string; current: string | null; onClose: () => void; onSaved: () => void }) {
  const toast = useUi(s => s.toast);
  const templates = useQuery({ queryKey: ['form-templates', workspaceId], queryFn: () => api.formTemplates(workspaceId) });
  const [value, setValue] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.setChannelForm(conversationId, value || null); toast(value ? 'Channel now uses the form' : 'Form removed - posts are free text', 'success'); onSaved(); onClose(); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Channel form" footer={<DialogFooter onCancel={onClose} onSave={() => void save()} saving={busy} />}>
      <div className="space-y-3 text-sm">
        <Select aria-label="Form template" value={value} onChange={e => setValue(e.target.value)}>
          <option value="">No form - free-text posts</option>
          {(templates.data?.templates ?? []).map(t => <option key={t.id} value={t.id}>{t.emoji ? `${t.emoji} ` : ''}{t.name}</option>)}
        </Select>
        <p className="text-slate-500">With a form, every new post in this channel must be a filled-in form (the server enforces this). Thread replies and reactions stay free text. Existing posts are not changed.</p>
        <p className="text-xs text-slate-500">Templates are built under <a className="link" href="/forms">Channel forms</a>.</p>
      </div>
    </Modal>
  );
}
